import type {SelectOption} from 'src/api/ProactApiClient';
import {VideoProviderTypeEnum} from 'src/api/ProactApiClient';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {MONTH_NAMES, RENEWAL_SCHEDULE} from './courseDefaults';
import {buildDefaultLanguageValidation, languageNames} from './languageUtils';

export const CourseAssignmentFormMeta: IFormFieldV2[] = [
  {
    name: 'assignedAt',
    label: 'Assigned Date',
    type: 'date',
    required: false,
    size: 'xl',
    description:
      'The date this course was assigned to the trainee. This is automatically set when the assignment is created. Manual changes may be overwritten when the system processes new assignments.',
  },
  {
    name: 'dueAt',
    label: 'Due Date',
    type: 'date',
    required: false,
    size: 'xl',
    description:
      'The deadline for the trainee to complete this course. Calculated from the Assigned Date plus the course\'s "Due in Days" setting. Only applies while the course is not yet completed. After completion, this field is replaced by the Expiration Date for renewal tracking.',
  },
  {
    name: 'completedAt',
    label: 'Complete Date',
    type: 'date',
    required: false,
    size: 'xl',
    description:
      'The date the trainee successfully finished this course. This determines when the renewal period starts (if renewals are enabled). For example, if completed on Jan 1 with a 365-day renewal period, the course expires on Dec 31.',
  },
];

export const CourseSessionFormMeta: IFormFieldV2[] = [
  {
    name: 'startedAt',
    label: 'Start Date',
    type: 'date',
    required: false,
    size: 'xl',
    description: 'The date the trainee initiated this training session.',
  },
  {
    name: 'completedAt',
    label: 'Complete Date',
    type: 'date',
    required: false,
    size: 'xl',
    description:
      'The date the trainee completed this training session. Only applicable if the session is completed, otherwise it is ignored.',
  },
];

const MONTH_OPTIONS: ISelectOption[] = MONTH_NAMES.map((label, index) => ({
  id: String(index + 1),
  label,
}));

const scheduleModeVisibility = (mode: string): IFormFieldV2['visibility'] => ({
  isDefaultHidden: true,
  rules: [
    {
      offlineId: '1',
      targetFieldInvariantId: 'renewalScheduleType',
      fieldValue: mode,
      visibility: true,
    },
  ],
});

export const CourseFormMeta = (
  courses?: SelectOption[],
  contentLanguages: string[] = []
): IFormFieldV2[] => [
  {
    name: 'heading1',
    label: 'General Settings',
    type: 'heading',
    size: 'xl',
  },
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    size: 'xl',
    labelTooltip:
      'A clear, descriptive name for the course that will be visible to trainees and in reports.',
  },
  {
    name: 'projectId',
    label: 'Project',
    type: 'project',
    size: 'xl',
    labelTooltip:
      'Assign this course to a specific project, or leave as instance-wide to make it available to all projects.',
  },
  {
    name: 'defaultLanguage',
    label: 'Default Language',
    type: 'select',
    required: true,
    size: 'xl',
    options: Object.entries(languageNames).map(([code, name]) => ({
      id: code,
      label: name,
    })),
    labelTooltip:
      'The default language for this course. Content must exist for the default language. When a trainee starts the course, this language is pre-selected.',
    builderOptions: {
      defaultValue: 'en',
    },
    validation: buildDefaultLanguageValidation(contentLanguages),
  },
  {
    name: 'active',
    label: 'Active',
    type: 'switch',
    required: true,
    size: 'xl',
    labelTooltip:
      'When active, this course can be assigned to trainees and will appear in course lists. Deactivate a course to hide it from new assignments while preserving existing training records.',
    builderOptions: {
      defaultValue: 'true',
    },
  },
  {
    name: 'targetDependencies',
    label: 'Course Dependencies',
    type: 'select-multiple',
    required: false,
    size: 'xl',
    options: (courses || []) as ISelectOption[],
    labelTooltip:
      'Choose one or more course dependencies. If selected, those courses must be completed before the current course may be started. Dependencies must be assigned separately.',
  },
  {
    name: 'heading26',
    label: 'Completion Tracking Settings',
    type: 'heading',
    size: 'xl',
  },
  {
    name: 'dueInDays',
    label: 'Due in Days',
    type: 'number',
    required: false,
    size: 'xl',
    description: 'Days to complete once assigned. Blank means no deadline.',
    labelTooltip:
      'Number of days a trainee has to complete the course after it is assigned or after renewal is triggered. For example, for annual training where users must complete by the 1-year mark, set Renewal Period to 335 days and Due in Days to 30. This gives trainees a 30-day window to complete before the full year expires. Leave blank if there is no deadline.',
  },
  {
    name: 'heading2',
    label: 'Renewal Settings',
    type: 'heading',
    size: 'xl',
  },
  {
    name: 'isRenewalRequired',
    label: 'Is Renewal Required for Course',
    invariantId: 'isRenewalRequired',
    type: 'switch',
    required: true,
    size: 'xl',
    labelTooltip:
      'Enable this if trainees must periodically retake the course to maintain their certification. When enabled, completed courses will expire after the Renewal Period and trainees will need to complete the course again.',
    builderOptions: {
      defaultValue: 'false',
    },
  },
  {
    name: 'renewalScheduleType',
    label: 'Renewal Schedule',
    invariantId: 'renewalScheduleType',
    type: 'select',
    required: true,
    size: 'xl',
    options: [
      {
        id: RENEWAL_SCHEDULE.RELATIVE,
        label: 'Renews a fixed time after completion',
      },
      {id: RENEWAL_SCHEDULE.FIXED, label: 'Renews on a fixed date each year'},
    ],
    builderOptions: {
      defaultValue: RENEWAL_SCHEDULE.RELATIVE,
    },
    visibility: {
      isDefaultHidden: true,
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'isRenewalRequired',
          fieldValue: 'true',
          visibility: true,
        },
      ],
    },
  },
  {
    name: 'renewalPeriodDays',
    label: 'Renewal Period in Days',
    type: 'number',
    required: false,
    size: 'xl',
    description: 'Days after completion before the course expires.',
    labelTooltip:
      'Number of days after course completion before the certification expires and renewal is triggered. For annual training, set this to 335 days with a Due in Days of 30 - the trainee will be notified to renew after 335 days and have 30 days to complete, totaling 1 year from their last completion. When the renewal period ends, the course status changes to "expired" and the trainee must complete it again.',
    visibility: scheduleModeVisibility(RENEWAL_SCHEDULE.RELATIVE),
  },
  {
    name: 'fixedRenewalMonth',
    label: 'Renewal Month',
    invariantId: 'fixedRenewalMonth',
    type: 'select',
    required: true,
    size: 'xl',
    options: MONTH_OPTIONS,
    labelTooltip:
      'Re-opens for all assignees every year on this date; a completion the day before still re-opens.',
    visibility: scheduleModeVisibility(RENEWAL_SCHEDULE.FIXED),
  },
  {
    name: 'fixedRenewalDay',
    invariantId: 'fixedRenewalDay',
    label: 'Renewal Day',
    type: 'number',
    required: true,
    size: 'xl',
    // Static bound only; the backend rejects an impossible month/day pair.
    validation: {
      min: 1,
      max: 31,
    },
    visibility: scheduleModeVisibility(RENEWAL_SCHEDULE.FIXED),
  },
  {
    name: 'heading3',
    label: 'Attempt Lockout Settings',
    type: 'heading',
    size: 'xl',
  },
  {
    name: 'isAttemptLimited',
    label: 'Is Attempt Limited',
    type: 'switch',
    required: true,
    size: 'xl',
    labelTooltip:
      'Enable this to limit how many times a trainee can attempt the course within a given period. This prevents trainees from repeatedly retrying a course in a short time. When the attempt limit is reached, the trainee will be locked out until the lockout period ends.',
    builderOptions: {
      defaultValue: 'false',
    },
  },
  {
    name: 'attemptLimit',
    label: 'Attempt Limit Within Period',
    type: 'number',
    required: false,
    size: 'xl',
    labelTooltip:
      'Maximum number of times a trainee can attempt this course within the lockout period. For example, entering "3" allows up to 3 attempts before the trainee is locked out.',
  },
  {
    name: 'attemptLockoutPeriodDays',
    label: 'Attempt Limit Lockout Period in Days',
    type: 'number',
    required: false,
    size: 'xl',
    labelTooltip:
      'Number of days during which the attempt limit applies. For example, if set to "7" with an attempt limit of "3", the trainee can only attempt the course 3 times within any 7-day window. After being locked out, they must wait for the lockout period to reset before trying again.',
  },
  {
    name: 'heading4',
    label: 'Certificate Settings',
    type: 'heading',
    size: 'xl',
  },
  {
    name: 'isCertificateGenerated',
    label: 'Generate Course Certificate',
    type: 'switch',
    required: true,
    size: 'xl',
    labelTooltip:
      'Enable this to automatically generate a digital certificate when a trainee successfully completes the course. Certificates are viewable in the browser from the training record.',
    builderOptions: {
      defaultValue: 'false',
    },
  },
  {
    name: 'certificateAdditionalContent',
    label: 'Certificate Additional Content',
    type: 'text',
    required: false,
    size: 'xl',
    labelTooltip:
      'Optional text to include on the certificate. Use this for regulatory references, course codes, or additional compliance information that should appear on all certificates for this course.',
    validation: {
      maxLength: 5000,
    },
  },
  {
    name: 'heading8',
    label: 'Certifying Trainer Settings',
    type: 'heading',
    size: 'xl',
  },
  {
    name: 'certifyingTrainerName',
    label: 'Certifying Trainer Name',
    type: 'text',
    required: false,
    size: 'xl',
    labelTooltip:
      'If provided, the certifying trainer name will appear on all certificates for this course. This may be used to achieve compliance with OSHA, EPA, CFATS, or other organizations.',
  },
  {
    name: 'certifyingTrainerTitle',
    label: 'Certifying Trainer Title',
    type: 'text',
    required: false,
    size: 'xl',
    labelTooltip:
      'The job title or professional designation of the certifying trainer (e.g., "Safety Director", "Certified Industrial Hygienist"). This appears on all certificates for this course alongside the trainer name.',
  },
];

/**
 * Groups `CourseFormMeta`'s fields (minus its inline `heading` separators)
 * into the Build tab's Identity / Deadlines / Attempts / Certificate
 * SectionCards. Field membership only — rendering is done by the workspace
 * via `<ProactField name={...} />` against the same single form instance, so
 * grouping never needs its own defaults/validation copy.
 */
export const CourseSettingsFieldGroups: {title: string; fields: string[]}[] = [
  {
    title: 'Identity',
    fields: [
      'name',
      'projectId',
      'defaultLanguage',
      'active',
      'targetDependencies',
    ],
  },
  {
    title: 'Deadlines',
    fields: [
      'dueInDays',
      'isRenewalRequired',
      'renewalScheduleType',
      'renewalPeriodDays',
      'fixedRenewalMonth',
      'fixedRenewalDay',
    ],
  },
  {
    title: 'Attempts',
    fields: ['isAttemptLimited', 'attemptLimit', 'attemptLockoutPeriodDays'],
  },
  {
    title: 'Certificate',
    fields: [
      'isCertificateGenerated',
      'certificateAdditionalContent',
      'certifyingTrainerName',
      'certifyingTrainerTitle',
    ],
  },
];

export const LessonFormMeta: IFormFieldV2[] = [
  {
    name: 'headerText',
    label: 'Header',
    type: 'text',
    required: true,
    size: 'xl',
    placeholder: 'My Lesson Name',
    textboxOptions: {multiline: true, minRows: 1},
  },
  {
    name: 'bodyText',
    label: '',
    type: 'richtext',
    required: false,
    size: 'xl',
    description:
      'This field may be used for text based lesson content or specific video instructions.',
  },
  {
    name: 'timeLimit',
    label: 'Time Limit (Minutes)',
    type: 'number',
    required: true,
    size: 'xl',
    description:
      'Enter the time limit for this content. If the time limit is exceeded, the user will fail the content.',
    builderOptions: {
      defaultValue: '60',
    },
    validation: {
      min: -1,
      excludeValues: [0],
      customError: 'Enter -1 for no limit, or a time limit greater than 0.',
    },
  },
  {
    name: 'videoProviderType',
    label: 'Video Provider',
    invariantId: 'videoProviderType',
    type: 'select',
    required: true,
    size: 'xl',
    description:
      'Select the video provider for this content. To use an internal video, upload the video to the Video Library and select the video from the dropdown.',
    options: [
      {
        label: 'Internal',
        id: VideoProviderTypeEnum.Internal.toString(),
      },
      {
        label: 'Vimeo',
        id: VideoProviderTypeEnum.Vimeo.toString(),
      },
    ],
  },
  {
    name: 'videoId',
    label: 'Video',
    type: 'video-select',
    required: true,
    size: 'xl',
    description: 'Select the video to use for this content.',
    visibility: {
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'videoProviderType',
          fieldValue: VideoProviderTypeEnum.Vimeo.toString(),
          visibility: false,
        },
        {
          offlineId: '2',
          targetFieldInvariantId: 'videoProviderType',
          fieldValue: VideoProviderTypeEnum.Internal.toString(),
          visibility: true,
        },
      ],
    },
  },
  {
    name: 'videoUrl',
    label: 'Video URL',
    type: 'text',
    required: true,
    size: 'xl',
    description:
      "Insert the Vimeo Video URL. Currently, only Vimeo videos are supported. Vimeo videos must be on a business account for the 'Enable Video Controls' setting to work correctly. The URL should be formatted like: https://vimeo.com/896982535/2ec2cebdde. To get the URL for your video, open the video in Vimeo, use the 'Share Link' button (note: not the 'Share' button) to Copy the shareable link, and then remove the extra URL data so that it is formatted like the example URL.",
    validation: {
      // Mirrors the server rule (CourseDefinitionExtensions.TryProcessLesson):
      // the URL must contain "vimeo.com".
      pattern: '^https?://.*vimeo\\.com.*$',
      patternError:
        'Please enter a valid Vimeo URL. Example: https://vimeo.com/896982535',
    },
    visibility: {
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'videoProviderType',
          fieldValue: VideoProviderTypeEnum.Vimeo.toString(),
          visibility: true,
        },
        {
          offlineId: '2',
          targetFieldInvariantId: 'videoProviderType',
          fieldValue: VideoProviderTypeEnum.Internal.toString(),
          visibility: false,
        },
      ],
    },
  },
  {
    name: 'enableVideoActiveScreenMonitoring',
    label: 'Enable Video Active Screen Monitoring',
    type: 'switch',
    required: true,
    size: 'xl',
    description:
      'If enabled, the video will only progress while the video window is actively selected on the user computer.',
    builderOptions: {
      defaultValue: 'true',
    },
  },
  {
    name: 'enableVideoControls',
    label: 'Enable Video Controls',
    type: 'switch',
    required: true,
    size: 'xl',
    description:
      'If enabled, the user can use a progress bar to move within the parts of the video already watched. Seeking ahead of the furthest watched point is never allowed. Play, pause, and volume controls are always available.',
    builderOptions: {
      defaultValue: 'false',
    },
  },
  {
    name: 'enableVideoAutostart',
    label: 'Enable Video Autostart',
    type: 'switch',
    required: true,
    size: 'xl',
    description:
      'If enabled, the video automatically starts when the lesson is opened.',
    builderOptions: {
      defaultValue: 'false',
    },
  },
  {
    name: 'enableVideoResume',
    label: 'Enable Video Resume',
    type: 'switch',
    required: true,
    size: 'xl',
    description:
      'If enabled, the video progress will be stored so the user can resume it later.',
    builderOptions: {
      defaultValue: 'false',
    },
  },
];

// `hideFormSelect` drops the legacy "Form" field and its required-field
// validation entirely; demonstrations with a `skillId` use skill-based
// sign-off instead of the Form Builder/"Assigned Form" path.
export const DemonstrationFormMeta = (
  forms?: SelectOption[],
  personnel?: SelectOption[],
  userGroups?: SelectOption[],
  hideFormSelect = false
): IFormFieldV2[] => [
  {
    name: 'headerText',
    label: 'Header',
    type: 'text',
    required: true,
    size: 'xl',
    placeholder: 'My Demonstration Name',
    textboxOptions: {multiline: true, minRows: 1},
  },
  {
    name: 'bodyText',
    label: '',
    type: 'richtext',
    required: false,
    size: 'xl',
    description:
      'This field may be used for text based lesson content or specific video instructions.',
  },
  ...(hideFormSelect
    ? []
    : [
        {
          name: 'demonstrationFormDefinitionId',
          label: 'Form',
          type: 'select',
          required: true,
          size: 'xl',
          options: (forms || []) as ISelectOption[],
          description:
            'The form to use as the training demonstration. The form should be designed in Form Builder. The demonstration is created as an "Assigned Form" task.',
        } as IFormFieldV2,
      ]),
  {
    name: 'assignToUserGroupId',
    label: 'Assign Demonstration To User Group',
    type: 'select',
    required: false,
    size: 'xl',
    options: (userGroups || []) as ISelectOption[],
    description:
      'This field is optional. Assigned User group takes priority over Assigned User. If you do not select a user group, the supervisor of the trainee is assigned the demonstration. If no supervisor is found, the demonstration is assigned to any available system administrators. If you select a user group, they will be assigned a task to complete the demonstration instead.',
  },
  {
    name: 'assignToUserId',
    label: 'Assign Demonstration To User',
    type: 'select',
    required: false,
    size: 'xl',
    options: (personnel || []) as ISelectOption[],
    description:
      'This field is optional. If you do not select a user or user group, the supervisor of the trainee is assigned the demonstration. If no supervisor is found, the demonstration is assigned to any available system administrators. If you select a user, they will be assigned a task to complete the demonstration instead.',
  },
];

export const QuizFormMeta: IFormFieldV2[] = [
  {
    name: 'headerText',
    label: 'Header',
    type: 'text',
    required: true,
    size: 'xl',
    placeholder: 'My Quiz Name',
    textboxOptions: {multiline: true, minRows: 1},
  },
  {
    name: 'bodyText',
    label: '',
    type: 'richtext',
    required: false,
    size: 'xl',
    description:
      'This field may be used for text based quiz content or specific instructions.',
  },
  {
    name: 'timeLimit',
    label: 'Time Limit (Minutes)',
    type: 'number',
    required: true,
    size: 'xl',
    description:
      'Enter the time limit for this content. If the time limit is exceeded, the user will fail the content.',
    builderOptions: {
      defaultValue: '60',
    },
    validation: {
      min: -1,
      excludeValues: [0],
      customError: 'Enter -1 for no limit, or a time limit greater than 0.',
    },
  },
  {
    name: 'scorePassPercentage',
    label: 'Score Pass Percentage',
    type: 'number',
    required: false,
    size: 'xl',
    description:
      "Enter the score required to pass the quiz. The score should be a whole number (eg., 50). If the user's answers to the quiz questions do not total this amount or higher, they will fail the quiz.",
  },
  {
    name: 'enableGenerateCertificate',
    label: 'Enable Certificate Generation',
    type: 'switch',
    required: true,
    size: 'xl',
    builderOptions: {
      defaultValue: 'true',
    },
    description:
      'If enabled, the quiz will generate a certificate upon completion. The quiz pass/fail status will always be retained by the system, even if no certificate is generated.',
  },
  {
    name: 'enableShowCorrectAnswersInResults',
    label: 'Enable Show Correct Answers in Results',
    type: 'switch',
    required: true,
    size: 'xl',
    builderOptions: {
      defaultValue: 'false',
    },
    description:
      'If enabled, the quiz will show the correct answer for any incorrectly answered questions upon completion. The user will be able to see the correct answer before attempting the training again.',
  },
];

export const QuestionFormMeta: IFormFieldV2[] = [
  {
    name: 'labelText',
    label: 'Question Label',
    type: 'text',
    required: true,
    size: 'xl',
    placeholder: 'My Question',
    textboxOptions: {multiline: true, minRows: 1},
  },
  {
    name: 'bodyText',
    label: '',
    type: 'richtext',
    required: false,
    size: 'xl',
    description:
      'This field may be used for additional question content or specific instructions.',
  },
  {
    name: 'scoreValue',
    label: 'Score Value',
    type: 'number',
    required: true,
    size: 'xl',
    description:
      'Enter the score value of this question. The value should be a whole number (eg., 5). This value is used to weight the question against other questions in the quiz.',
  },
];

export const SessionOverviewFormMeta: IFormFieldV2[] = [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    size: 'xl',
  },
];
